'use client';
'use strict';

var _tslib = require('../../../_virtual/_tslib.js');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
var Box = require('../../Base/Layouts/Box/Box.js');

/**
 * 아이콘을 포함한 버튼 컴포넌트입니다.
 *
 * @component
 *
 * @param {Object} props
 * @param {'sm' | 'md' | 'lg'} [props.size='md'] - 버튼의 크기를 지정합니다
 *   - 'sm': 36px
 *   - 'md': 50px
 *   - 'lg': 60px
 * @param {boolean} [props.outline=false] - 외곽선 스타일 적용 여부를 지정합니다
 * @param {boolean} [props.disabled=false] - 버튼 비활성화 여부를 지정합니다
 * @param {React.ReactNode} props.children - 버튼 내부에 표시될 아이콘 컴포넌트
 * @param {string} [props.className] - 추가적인 CSS 클래스
 * @param {function} [props.onClick] - 클릭 이벤트 핸들러
 *
 * @example
 * // 기본 사용
 * <IconButton>
 *   <Icon name="search" />
 * </IconButton>
 *
 * // 크기 변경
 * <IconButton size="lg">
 *   <Icon name="plus" />
 * </IconButton>
 *
 * // 외곽선 스타일
 * <IconButton outline>
 *   <Icon name="menu" />
 * </IconButton>
 */ const IconButton = (_a)=>{
    var { size = 'md', outline = false, children, onClick, className, disabled } = _a, rest = _tslib.__rest(_a, [
        "size",
        "outline",
        "children",
        "onClick",
        "className",
        "disabled"
    ]);
    const [isClicked, setIsClicked] = react.useState(false);
    // eslint-disable-next-line no-undef
    const timeoutRef = react.useRef(null);
    // TODO: 크기 변경 피그마 싱크
    const generateContainerSize = ()=>{
        if (size === 'sm') return 'size-6 rounded-lg';
        if (size === 'md') return 'size-9';
        if (size === 'lg') return 'size-[50px]';
    };
    const generateContainerStyle = ()=>{
        if (outline) return 'border-w-gray-200 overflow-hidden';
        return 'border-transparent';
    };
    const generateAnimationStyle = ()=>{
        if (outline && size === 'sm') return 'animate-[rippleOutlineSmall_0.3s_ease-in-out_infinite]';
        if (outline) return 'animate-[rippleOutline_0.3s_ease-in-out_infinite]';
        return 'animate-[ripple_0.3s_ease-in-out_infinite]';
    };
    const onClickHandler = (e)=>{
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        setIsClicked(true);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(()=>{
            setIsClicked(false);
        }, 300);
    };
    react.useEffect(()=>{
        return ()=>{
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);
    return jsxRuntime.jsxs("button", Object.assign({
        "data-testid": "design-system-icon-button",
        type: "button",
        className: tailwindMerge.twMerge('relative flex items-center justify-center rounded-xl border bg-transparent', disabled ? 'cursor-not-allowed' : 'cursor-pointer', generateContainerSize(), generateContainerStyle(), className),
        onClick: onClickHandler,
        disabled: disabled
    }, rest, {
        children: [
            isClicked && jsxRuntime.jsx(Box, {
                "data-testid": "design-system-icon-button--ripple",
                className: tailwindMerge.twMerge('absolute h-full w-full bg-w-gray-500 bg-opacity-10', generateAnimationStyle())
            }),
            jsxRuntime.jsx(Box, {
                "data-testid": "design-system-icon-button--icon",
                className: "absolute flex h-full w-full items-center justify-center",
                children: children
            })
        ]
    }));
};

module.exports = IconButton;
